import { useSelector } from "react-redux";
import {
  AddIcon,
  RemoveIcon,
  EditOutlinedIcon,
  DeleteOutlineIcon,
} from "./icons";
import { selectStatefulCosts } from "../ducks/checkoutSlice";
import toPriceDisplay from "../utils/toPriceDisplay";
import qttDisplay from "../utils/qttDisplay";
import UnavailableDeliveryWarning from "./UnavailableDeliveryWarning";

/**
 * Sacola do totem e do tablet: cada item num cartão com as escolhas, a
 * quantidade e as ações à vista (editar, remover), e o subtotal no fim.
 * Os itens vêm de utils/cartItems; as ações, do CartView.
 */
export default function ProductsListCartTotem({
  products,
  onEdit,
  onRemove,
  onIncrement,
  onDecrement,
}) {
  const costs = useSelector(selectStatefulCosts);
  const itemsLabel =
    products.length === 1 ? "1 item" : `${products.length} itens`;

  return (
    <div className="totem-cart">
      <ul className="totem-cart__list">
        {products.map((product) => (
          <li key={product.uuid}>
            <CartItem
              product={product}
              onEdit={onEdit}
              onRemove={onRemove}
              onIncrement={onIncrement}
              onDecrement={onDecrement}
            />
          </li>
        ))}
      </ul>
      <div className="totem-cart__summary">
        <span className="totem-cart__count">{itemsLabel}</span>
        <span className="totem-cart__total-label">Subtotal</span>
        <strong className="totem-cart__total">
          {toPriceDisplay(costs.subtotal)}
        </strong>
      </div>
      <UnavailableDeliveryWarning />
    </div>
  );
}

function CartItem({ product, onEdit, onRemove, onIncrement, onDecrement }) {
  const { error } = product;
  const hasError = error.isDisabled || error.isPriceChanged;

  return (
    <article className={`totem-cart-item${hasError ? " has-error" : ""}`}>
      {product.pictureUrl && (
        <img src={product.pictureUrl} alt="" className="totem-cart-item__img" />
      )}
      <div className="totem-cart-item__body">
        <div className="totem-cart-item__head">
          <h3 className="totem-cart-item__name">{product.productName}</h3>
          <span className="totem-cart-item__price">
            {toPriceDisplay(product.price)}
          </span>
        </div>

        {(error.isDisabled || error.isPriceChanged) && (
          <div className="totem-cart-item__alerts">
            {error.isDisabled && (
              <span className="totem-chip totem-chip--danger">
                Indisponível
              </span>
            )}
            {error.isPriceChanged && (
              <span className="totem-chip totem-chip--danger">
                {`Preço mudou: ${
                  error.priceDiff.gt(0) ? "+" : "-"
                } ${toPriceDisplay(error.priceDiff.abs())}`}
              </span>
            )}
          </div>
        )}

        {product.detailsList && product.detailsList.length > 0 && (
          <ul className="totem-cart-item__details">
            {product.detailsList.map((detail, index) => (
              <li
                key={index}
                className={detail.error.isDisabled ? "is-unavailable" : ""}
              >
                {detail.name}
                {detail.error.isDisabled && " (indisponível)"}
              </li>
            ))}
          </ul>
        )}

        <div className="totem-cart-item__actions">
          <div className="totem-stepper totem-stepper--compact">
            <button
              type="button"
              className="totem-stepper__button"
              onClick={() => onDecrement(product)}
              disabled={product.qtt <= product.qttMinimum}
              aria-label={`Diminuir ${product.productName}`}
            >
              <RemoveIcon />
            </button>
            <span className="totem-stepper__value">
              {qttDisplay(product.qtt, product.isKilogram)}
            </span>
            <button
              type="button"
              className="totem-stepper__button"
              onClick={() => onIncrement(product)}
              disabled={error.isDisabled}
              aria-label={`Aumentar ${product.productName}`}
            >
              <AddIcon />
            </button>
          </div>
          {!error.isDisabled && (
            <button
              type="button"
              className="totem-text-button"
              onClick={() => onEdit(product)}
              aria-label={`Editar ${product.productName}`}
            >
              <EditOutlinedIcon />
              <span className="totem-text-button__label">Editar</span>
            </button>
          )}
          <button
            type="button"
            className="totem-text-button totem-text-button--danger"
            onClick={() => onRemove(product)}
            aria-label={`Remover ${product.productName}`}
          >
            <DeleteOutlineIcon />
            <span className="totem-text-button__label">Remover</span>
          </button>
        </div>
      </div>
    </article>
  );
}
